import {
  GetExperimentRequest,
  UntagExperimentRequest,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_service_pb';
import { FilterRequest } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/listing_pb';
import { Operator } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/query_pb';
import { RegisteredModel } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_pb';
import { ListRegisteredModelsResponse } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_service_pb';
import { RegisteredModelVersion } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_pb';
import { ListModelVersionsForModelRequest } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_service_pb';
import { Tag } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/tag_pb';
import { ExperimentService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/experiment_service_connect';
import { RegisteredModelService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_service_connect';
import { RegisteredModelVersionService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_version_service_connect';
import { TagService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/tag_service_connect';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { IStyle, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesPrimary,
  ClassNamesFromIStyles,
  IH2OTheme,
  Loader,
  TextField,
  useClassNames,
  useHaicPageTitle,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import React from 'react';

import { FailedToLoadView } from '../../components/FailedToLoadView/FailedToLoadView';
import Header from '../../components/Header/Header';
import { NoItemView } from '../../components/NoItemView/NoItemView';
import WidgetList from '../../components/WidgetList/WidgetList';
import { useCloudPlatformDiscovery, useEnv } from '../../utils/hooks';
import { formatDefaultDateAndTime, formatDefaultDateAndTimeWithSeconds, formatError } from '../../utils/utils';
import { ContextMenuIconButton } from '../Orchestrator/Workflows';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { ENDPOINTS } from './constants';
import { RegisteredModelVersionWithTag } from './ModelTaggingWidget';
import NavigationWrapper from './NavigationWrapper';
import PageWrapper from './PageWrapper';
import { useGranularMLOpsPermissions } from './userActions';

type RegisteredModelVersions = Record<string, RegisteredModelVersionWithTag[]>;
export interface ITagsWidgetStyles {
  loaderWrapper: IStyle;
  createTagContainer: IStyle;
  buttons: IStyle;
}

export const tagsWidgetStyles = (theme: IH2OTheme): Partial<ITagsWidgetStyles> => ({
  loaderWrapper: {
    height: 360,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  createTagContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '10px 10px',
    margin: '0px 20px',
    backgroundColor: theme.semanticColors?.bodyBackground,
  },
  buttons: {
    display: 'flex',
    gap: 8,
    marginTop: 12,
  },
});

type TagItem = Tag & {
  onDeleteTag?: () => Promise<void>;
  onEditTag?: () => void;
  createTime?: string;
  viewOnly?: boolean;
  modelVersionCount?: number;
};

const columns = [
  {
    key: 'title',
    name: 'Title',
    fieldName: '',
    minWidth: 200,
    data: {
      headerFieldName: 'displayName',
      listCellProps: {
        onRenderHeader: ({ displayName, id }: TagItem) => (
          <span
            style={{
              padding: '2px 4px',
              background: 'lightgray',
              borderRadius: 24,
              marginRight: 2,
              lineHeight: '22px',
            }}
            key={id}
          >
            {displayName}
          </span>
        ),
      },
    },
  },
  {
    key: 'createdAt',
    name: 'Created at',
    fieldName: 'createTime',
    minWidth: 150,
    maxWidth: 250,
  },
  {
    key: 'modelVersionCount',
    name: 'Used by',
    fieldName: 'modelVersionCount',
    minWidth: 150,
    maxWidth: 230,
    data: {
      listCellProps: {
        onRenderText: ({ modelVersionCount }: TagItem) => <>{`${modelVersionCount} model versions`}</>,
      },
    },
  },
  {
    key: 'buttons',
    name: '',
    minWidth: 200,
    data: {
      listCellProps: {
        emptyMessage: '',
        onRenderText: ({ onDeleteTag, onEditTag, viewOnly }: TagItem) =>
          // TODO: Use theme prop for colors.
          viewOnly ? null : (
            <ContextMenuIconButton
              items={[
                {
                  key: 'delete',
                  text: 'Delete',
                  onClick: () => {
                    (async () => {
                      await onDeleteTag?.();
                    })();
                  },
                  style: { color: 'var(--h2o-red400)', display: viewOnly ? 'none' : undefined },
                  iconProps: {
                    iconName: 'Delete',
                    style: { color: 'var(--h2o-red400)' },
                  },
                },
                {
                  key: 'edit',
                  text: 'Edit',
                  onClick: onEditTag,
                  iconProps: { iconName: 'Edit', style: { color: 'var(--h2o-gray900)' } },
                  style: { color: 'var(--h2o-gray900)' },
                },
              ]}
            />
          ),
        styles: {
          root: {
            display: 'flex',
            flexGrow: 1,
            justifyContent: 'end',
          },
        },
      },
    },
  },
];

export const getFilter = (filter: string, propertyName: string, operator: Operator) =>
  ({
    query: {
      clause: [
        {
          propertyConstraint: [
            {
              property: {
                propertyType: {
                  value: propertyName,
                  case: 'field',
                },
              },
              operator,
              value: {
                value: {
                  case: 'stringValue',
                  value: filter,
                },
              },
            },
          ],
        },
      ],
    },
  } as FilterRequest);

const TagsManagement = () => {
  const theme = useTheme(),
    env = useEnv(),
    classNames = useClassNames<ITagsWidgetStyles, ClassNamesFromIStyles<ITagsWidgetStyles>>(
      'tagsWidgetStyles',
      tagsWidgetStyles(theme)
    ),
    { addToast } = useToast(),
    [loading, setLoading] = React.useState(true),
    [isLoadingMore, setIsLoadingMore] = React.useState(false),
    [isLoadingSearch, setIsLoadingSearch] = React.useState(false),
    [isSearchStr, setIsSearchStr] = React.useState(false),
    [nextPageToken, setNextPageToken] = React.useState<Uint8Array>(),
    [lastRefreshed, setLastRefreshed] = React.useState<Date>(),
    { ACTIVE_WORKSPACE_NAME, ACTIVE_WORKSPACE_ID } = useWorkspaces(),
    permissions = useGranularMLOpsPermissions(ACTIVE_WORKSPACE_NAME),
    [registeredModels, setRegisteredModels] = React.useState<RegisteredModel[]>(),
    [registeredModelVersions, setRegisteredModelVersions] = React.useState<RegisteredModelVersions>(),
    [tags, setTags] = React.useState<Tag[]>(),
    [tagItems, setTagItems] = React.useState<TagItem[]>(),
    [newTagName, setNewTagName] = React.useState(''),
    [isCreateNewTag, setIsCreateNewTag] = React.useState(false),
    [tagUpdateId, setTagUpdateId] = React.useState(''),
    loadStateRef = React.useRef({
      loadingTags: false,
      creatingTag: false,
      fetchRegisteredModels: false,
      fetchModelVersions: false,
      settingVersionItems: false,
      updatingTag: false,
    }),
    evaluateLoading = () => {
      if (
        !loadStateRef.current.loadingTags &&
        !loadStateRef.current.creatingTag &&
        !loadStateRef.current.fetchRegisteredModels &&
        !loadStateRef.current.fetchModelVersions &&
        !loadStateRef.current.settingVersionItems &&
        !loadStateRef.current.updatingTag
      ) {
        setLoading(false);
        // TODO: Check if this is correct.
        setLastRefreshed(new Date());
      }
    },
    // TODO: Move to context global to mlops.
    cloudPlatformDiscovery = useCloudPlatformDiscovery(),
    mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '',
    storageTransport = createConnectTransport({
      baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
    }),
    tagClient = createClient(TagService, storageTransport),
    experimentClient = createClient(ExperimentService, storageTransport),
    registeredModelClient = createClient(RegisteredModelService, storageTransport),
    registerModelVersionClient = createClient(RegisteredModelVersionService, storageTransport),
    getExperiment = React.useCallback(
      async (experimentId: string) => {
        try {
          const response = await experimentClient.getExperiment(
            new GetExperimentRequest({
              id: experimentId,
            })
          );
          const experiment = response?.experiment;
          if (!experiment) console.error('No experiment found in the response.');
          return experiment;
        } catch (err) {
          const message = `Failed to fetch experiment metadata: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        }
      },
      [addToast]
    ),
    fetchRegisteredModels = React.useCallback(async () => {
      if (!ACTIVE_WORKSPACE_ID) return;
      loadStateRef.current.fetchRegisteredModels = true;
      setLoading(true);
      try {
        let regModelItems: RegisteredModel[] = [],
          morePagesAvailable = true,
          nextPageToken;

        while (morePagesAvailable) {
          const data: ListRegisteredModelsResponse = await registeredModelClient.listRegisteredModels({
            // TODO: Use ACTIVE_WORKSPACE_NAME directly once storage is migrated.
            projectId: ACTIVE_WORKSPACE_ID,
            paging: {
              pageToken: nextPageToken,
            },
          });
          const nextPage = data?.paging?.nextPageToken;
          regModelItems = [...regModelItems, ...(data?.registeredModels || [])];
          morePagesAvailable = !!nextPage;
          nextPageToken = nextPage;
        }

        if (!regModelItems) console.error('No registered models found in the response.');
        setRegisteredModels(regModelItems);
      } catch (err) {
        const message = `Failed to fetch registered models: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        setRegisteredModels(undefined);
      } finally {
        loadStateRef.current.fetchRegisteredModels = false;
        // HACK: Set the loading of settingVersionItems to true to prevent the loading spinner from disappearing.
        loadStateRef.current.settingVersionItems = true;
        evaluateLoading();
      }
    }, [addToast, ACTIVE_WORKSPACE_ID]),
    listModelVersionsForModel = React.useCallback(
      async (registeredModelId: string, modelName: string) => {
        loadStateRef.current.fetchModelVersions = true;
        try {
          const listModelVersionsForModelBody = new ListModelVersionsForModelRequest({
            registeredModelId,
          });
          const response = await registerModelVersionClient.listModelVersionsForModel(listModelVersionsForModelBody);
          const modelVersions = response?.modelVersions;
          if (!modelVersions) console.error('No model versions found in the response.');

          const modelVersionsWithTags: RegisteredModelVersionWithTag[] = [];
          for (const item of modelVersions) {
            const versionExperiment = await getExperiment(item.experimentId);
            const tag = versionExperiment?.tag;
            if (tag) {
              // TODO: Avoid Object.assign.
              modelVersionsWithTags.push(Object.assign(new RegisteredModelVersion(item), { tag }));
            } else {
              modelVersionsWithTags.push(item);
            }
          }

          return modelVersionsWithTags;
        } catch (err) {
          const message = `Failed to fetch model versions for ${modelName} (${registeredModelId}): ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        } finally {
          loadStateRef.current.fetchModelVersions = false;
          evaluateLoading();
        }
      },
      [addToast, registerModelVersionClient]
    ),
    listVersionsForAllModels = React.useCallback(async () => {
      if (!registeredModels) return;
      const allModelVersions: RegisteredModelVersions = {};
      for (const item of registeredModels) {
        const modelVersions = await listModelVersionsForModel(item.id, item.displayName);
        if (modelVersions) allModelVersions[item.id] = modelVersions;
      }
      // TODO: Sort by first model version creation time as fetch calls may not be in order.
      setRegisteredModelVersions(allModelVersions);
    }, [registeredModels]),
    fetchTags = React.useCallback(
      async (pageToken?: Uint8Array, filter?: string) => {
        if (!ACTIVE_WORKSPACE_ID) return;
        loadStateRef.current.loadingTags = true;
        if (pageToken) setIsLoadingMore(true);
        else if (filter || filter === `""`) setIsLoadingSearch(true);
        else setLoading(true);

        try {
          const response = await tagClient.listTags({
            // TODO: Use ACTIVE_WORKSPACE_NAME directly once storage is migrated.
            projectId: ACTIVE_WORKSPACE_ID,
            filter: filter ? getFilter(filter, 'display_name', Operator.CONTAINS) : undefined,
            // TODO: Fetch all pages.
            // paging: {
            //   pageToken
            // },
          });
          const tags = response?.tag;
          if (!tags) console.error('No tags found in the response.');
          setTags(tags);
          setNextPageToken(response?.paging?.nextPageToken);
        } catch (err) {
          const message = `Failed to fetch tags: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          setTags(undefined);
        } finally {
          loadStateRef.current.loadingTags = false;
          evaluateLoading();
          setIsLoadingMore(false);
          setIsLoadingSearch(false);
        }
      },
      [ACTIVE_WORKSPACE_ID]
    ),
    untagExperiment = React.useCallback(async (experimentId: string, tagId: string) => {
      try {
        await experimentClient.untagExperiment(
          new UntagExperimentRequest({
            experimentId,
            tagId,
          })
        );
      } catch (err) {
        const message = `Failed to untag experiment: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      }
    }, []),
    deleteTag = React.useCallback(
      async (id: string) => {
        loadStateRef.current.creatingTag = true;
        setLoading(true);
        try {
          await tagClient.deleteTag({
            // TODO: Use ACTIVE_WORKSPACE_NAME directly once storage is migrated.
            projectId: ACTIVE_WORKSPACE_ID,
            id,
          });
          addToast({
            messageBarType: MessageBarType.success,
            message: 'Tag deleted successfully.',
          });
          await fetchTags();
        } catch (err) {
          const message = `Failed to delete tag: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          loadStateRef.current.creatingTag = false;
          evaluateLoading();
        }
      },
      [ACTIVE_WORKSPACE_ID, fetchTags, untagExperiment]
    ),
    createTag = React.useCallback(
      async (tagName: string) => {
        loadStateRef.current.creatingTag = true;
        try {
          await tagClient.createTag({
            // TODO: Use ACTIVE_WORKSPACE_NAME directly once storage is migrated.
            projectId: ACTIVE_WORKSPACE_ID,
            tag: new Tag({
              displayName: tagName,
            }),
          });
          await fetchTags();
          setNewTagName('');
          setTagUpdateId('');
          setIsCreateNewTag(false);
        } catch (err) {
          const message = `Failed to create tag: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          loadStateRef.current.creatingTag = false;
          evaluateLoading();
        }
      },
      [ACTIVE_WORKSPACE_ID, fetchTags]
    ),
    updateTag = React.useCallback(
      async (tagId: string, tagName: string) => {
        loadStateRef.current.updatingTag = true;
        try {
          await tagClient.updateTag({
            tag: new Tag({
              id: tagId,
              displayName: tagName,
              projectId: ACTIVE_WORKSPACE_ID,
            }),
            updateMask: {
              paths: ['display_name'],
            },
          });
          setTagUpdateId('');
          setNewTagName('');
          setIsCreateNewTag(false);
          await fetchTags();
        } catch (err) {
          const message = `Failed to update tag: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          loadStateRef.current.updatingTag = false;
          evaluateLoading();
        }
      },
      [ACTIVE_WORKSPACE_ID, fetchTags]
    ),
    refreshItems = () => {
      void fetchTags();
      setIsSearchStr(false);
    },
    onAction = () => {
      setIsCreateNewTag(true);
    },
    headerActionProps = React.useMemo(
      () =>
        permissions?.isAbleToChangeModelTags
          ? {
              actionTitle: 'New tag',
              onActionClick: onAction,
              actionIcon: 'Add',
            }
          : undefined,
      [permissions?.isAbleToChangeModelTags, onAction]
    ),
    widgetListProps = {
      columns,
      items: tagItems,
      loading: !!loading,
      delayLoader: false,
      isLoadingMore: isLoadingMore,
      onLoadMore: nextPageToken ? () => void fetchTags(nextPageToken) : undefined,
      isLoadingSearch,
      lastRefreshed: formatDefaultDateAndTimeWithSeconds(lastRefreshed),
      onRefresh: refreshItems,
      searchProps: {
        placeholder: 'Search tags',
        onSearchChange: (value: string) => {
          setIsSearchStr(!!value);
          void fetchTags(undefined, value);
        },
      },
      NoItemsContent: NoItemView({
        title: 'No tags',
        description: 'There are no tags to display.',
        ...headerActionProps,
      }),
      ErrorContent: FailedToLoadView({
        title: 'Failed to load tags',
        description: 'Please try again later. If the problem persists, contact our support.',
        actionTitle: 'Retry',
        onActionClick: fetchTags,
        actionIcon: 'Refresh',
      }),
    };

  React.useEffect(() => {
    if (ACTIVE_WORKSPACE_ID) fetchTags();
  }, [fetchTags, ACTIVE_WORKSPACE_ID]);

  React.useEffect(() => void fetchRegisteredModels(), [fetchRegisteredModels]);

  React.useEffect(() => {
    if (registeredModels) {
      void listVersionsForAllModels();
    }
  }, [registeredModels]);

  useHaicPageTitle('Tags Management | MLOps Admin', env?.cloudInstanceName);

  React.useEffect(() => {
    if (!registeredModels || !registeredModelVersions || !tags) return;
    if (!isLoadingSearch) setLoading(true);
    loadStateRef.current.settingVersionItems = true;

    const versions = Object.values(registeredModelVersions).flat();
    const tagCount = versions.reduce((acc, version) => {
      const tag = version.tag;
      tag?.forEach((t) => {
        acc[t.id] = (acc[t.id] || 0) + 1;
      });
      return acc;
    }, {} as Record<string, number>);
    const tagExperimentIds = versions.reduce((acc, version) => {
      const tag = version.tag;
      tag?.forEach((t) => {
        acc[t.id] = (acc[t.id] || []).concat(version.experimentId);
      });
      return acc;
    }, {} as Record<string, string[]>);
    const tagItems = tags?.map((tag) => {
      const modelVersionCount = tagCount?.[tag.id] || 0;
      return {
        ...tag,
        modelVersionCount,
        createTime:
          tag.createdTime?.seconds !== undefined
            ? formatDefaultDateAndTime(Number(tag.createdTime.seconds) * 1000)
            : '',
        viewOnly: !permissions?.isAbleToChangeModelTags,
        onDeleteTag: () => {
          (async () => {
            if (Object.keys(tagExperimentIds || {})?.length) {
              await Promise.all(tagExperimentIds[tag.id]?.map((experimentId) => untagExperiment(experimentId, tag.id)));
            }
            await deleteTag(tag.id);
          })();
        },
        onEditTag: () => {
          setTagUpdateId(tag.id);
          setNewTagName(tag.displayName);
        },
      };
    }) as TagItem[];

    setTagItems(tagItems);

    loadStateRef.current.settingVersionItems = false;
    evaluateLoading();
  }, [registeredModelVersions, registeredModels, tags]);

  return (
    <NavigationWrapper>
      <PageWrapper>
        {tags || isSearchStr || loading ? (
          <Header customPageTitle="Manage model tags" {...(tags?.length ? headerActionProps : {})} />
        ) : null}
        {loading ? (
          <div className={classNames.loaderWrapper}>
            <Loader label="Loading tags..." />
          </div>
        ) : (
          <>
            {permissions?.isAbleToChangeModelTags && (isCreateNewTag || tagUpdateId) ? (
              <div className={classNames.createTagContainer}>
                <TextField
                  label="Tag name"
                  required
                  onChange={(_ev, value) => {
                    setNewTagName(value || '');
                  }}
                  value={newTagName}
                  disabled={!permissions?.isAbleToChangeModelTags}
                  styles={{ root: { maxWidth: 360 } }}
                />
                <div className={classNames.buttons}>
                  <Button
                    primary
                    text="Cancel"
                    onClick={() => {
                      setIsCreateNewTag(false);
                      setTagUpdateId('');
                      setNewTagName('');
                    }}
                  />
                  <Button
                    primary
                    styles={buttonStylesPrimary}
                    text={tagUpdateId ? 'Update tag' : 'Create tag'}
                    onClick={() => {
                      tagUpdateId ? updateTag(tagUpdateId, newTagName) : createTag(newTagName);
                    }}
                    disabled={!newTagName}
                  />
                </div>
              </div>
            ) : null}
            {tagItems && tagItems.length === 0 && isCreateNewTag ? null : <WidgetList {...widgetListProps} />}
          </>
        )}
      </PageWrapper>
    </NavigationWrapper>
  );
};

export default TagsManagement;
